// =============================================================================
// components/LifeProfileBanner.js — Phase 74A (post-user-feedback)
//
// Dashboard "Help 2120 understand you" card. Reframed from a nag banner
// to a *value-forward* tile that shows what completing the profile
// unlocks. Never guilt-trips; disappears at 100% completion.
//
// Shown when:
//   • The user's profile completeness is under 100%
//   • The user hasn't dismissed the tile in the last 7 days
//
// The tile never blocks; it's purely additive. Dismissing snoozes for
// 7 days server-side, so the same state persists across devices.
//
// "Prefer not to say" counts as answered — so a privacy-conscious user
// can still reach 100% and have the tile go away.
// =============================================================================
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Sparkles, ArrowRight, X, Check } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';

import { supabase } from '@/lib/supabaseClient';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const UNLOCKS = [
  'Better projections',
  'More personalised reflections',
  'Smarter AI answers',
];

export default function LifeProfileBanner() {
  const nav = useNavigate();
  const [state, setState] = useState({ loading: true, show: false, pct: 0, tier: 'Basic' });
  const [dismissing, setDismissing] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.access_token) {
          setState({ loading: false, show: false, pct: 0, tier: 'Basic' });
          return;
        }
        const { data } = await axios.get(`${BACKEND_URL}/api/life-profile`, {
          headers: { Authorization: `Bearer ${session.access_token}` },
          timeout: 8000,
        });
        setState({
          loading: false,
          show:    !!data?.show_dashboard_banner,
          pct:     Math.round(Number(data?.completeness || 0) * 100),
          tier:    String(data?.profile_quality || 'Basic'),
        });
      } catch (e) {
        // Endpoint may 503 if the migration hasn't been applied yet, or
        // the user might not have a Bearer token. Either way, hide silently.
        setState({ loading: false, show: false, pct: 0, tier: 'Basic' });
      }
    })();
  }, []);

  const dismiss = async () => {
    if (dismissing) return;
    setDismissing(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      await axios.post(
        `${BACKEND_URL}/api/life-profile/dismiss-banner`,
        {},
        { headers: { Authorization: `Bearer ${session?.access_token}` }, timeout: 8000 },
      );
    } catch (e) {
      // Non-fatal; hide locally either way.
    } finally {
      setState((s) => ({ ...s, show: false }));
      setDismissing(false);
    }
  };

  if (state.loading || !state.show) return null;

  return (
    <Card
      className="rounded-2xl border-primary/25 bg-primary/[0.04]"
      data-testid="life-profile-banner"
    >
      <CardContent className="p-4 sm:p-5 flex flex-col gap-4">
        {/* Top row — title + tier badge + dismiss */}
        <div className="flex items-start gap-3">
          <div className="h-9 w-9 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center shrink-0">
            <Sparkles className="h-4 w-4 text-primary" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <div className="text-sm font-medium" data-testid="life-profile-banner-title">
                Help 2120 understand you
              </div>
              <Badge
                variant="outline"
                className="rounded-full text-[10px] border-primary/30 text-primary"
                data-testid="life-profile-banner-tier"
              >
                {state.tier}
              </Badge>
            </div>
            <div className="text-xs text-muted-foreground leading-relaxed mt-1">
              You&apos;ve completed <span className="font-medium text-foreground" data-testid="life-profile-banner-pct">{state.pct}%</span> of your Financial Profile.
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={dismiss}
            disabled={dismissing}
            className="rounded-xl text-muted-foreground -mr-1 -mt-1 shrink-0"
            aria-label="Dismiss for a week"
            data-testid="life-profile-banner-dismiss"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* Progress */}
        <Progress value={state.pct} className="h-1.5" data-testid="life-profile-banner-progress" />

        {/* Unlocks list */}
        <div className="space-y-1.5">
          <div className="text-[11px] uppercase tracking-widest text-muted-foreground">
            Complete it to unlock
          </div>
          <ul className="space-y-1">
            {UNLOCKS.map((u) => (
              <li key={u} className="flex items-center gap-2 text-xs text-foreground/85" data-testid={`life-profile-banner-unlock-${u.replace(/\s+/g, '-').toLowerCase()}`}>
                <Check className="h-3.5 w-3.5 text-primary shrink-0" />
                <span>{u}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* CTA */}
        <div className="flex items-center justify-end">
          <Button
            onClick={() => nav('/settings/life-profile')}
            className="rounded-xl"
            data-testid="life-profile-banner-cta"
          >
            Complete profile <ArrowRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
